import * as PropTypes             from 'prop-types';
import { connect }                from 'react-redux';
import {
    compose,
    branch,
    lifecycle,
    renderComponent,
    withHandlers,
    withProps,
    withState,
    getContext
}                                 from 'recompose';
import moment                     from 'moment';
import { withRouter }             from 'react-router';
import {
    listenMessages,
    loadMessages,
    sendMessage,
    updateMessages,
    unlistenMessages
}                                 from './MessagesActions';
import { updateUnreadCount }      from '../ConversationsList/ConversationsListActions';
import {
    getMessagesFeed,
    getWaitingForReplyStatus
}                                 from './MessagesSelectors';
import socket                     from '../../services/SocketService';
import cache, { ONE_YEAR }        from '../../services/CacheService';

// components
import MessagesFeedComponent      from './MessagesFeedComponent';
import MessagesFeedLoader         from './MessagesFeedLoader';
import MessageTemplatesList       from '../MessageTemplatesList';
import { MessagesContainerProps } from './MessagesFeedTypes';
import {
    MessagesFeed,
    AppMessengerSocketError,
    SocketMessages
}                                 from '../../types';
import { ViewContextTypes }       from '../MessageTemplatesList/MessageTemplatesListTypes';
import { textToTranslationKey }   from '../../helpers/stringUtils';
import { getContactData }         from '../Contact/ContactSelector';

const mapStateToProps = (state, props: MessagesContainerProps) => {
    return {
        contact: getContactData(state, props),
        messagesFeed: getMessagesFeed(state, props),
        waitingForReplyStatus: getWaitingForReplyStatus(state, props)
    };
};

const isLoading = props => !(props.messagesFeed);

// only show chat starters if the conversation has no message history
// const showMessageTemplates = props => props.messagesFeed.total_count === 0;
// TODO: change this when total_count property is fixed
// Only load message template list component when there are no message history and we haven't checked that there are templates.
// If there are no templates, default to message feed.
const showMessageTemplates = props => (props.messagesFeed.messages.length === 0 && !props.waitingForReplyStatus) && (props.haveMessageTemplates === undefined || props.haveMessageTemplates);

const mapDispatchers = (dispatch, ownProps: MessagesContainerProps) => ({
    loadMessages(hasNextConvoState?: boolean) {
        dispatch(loadMessages(ownProps.conversationId, hasNextConvoState));
    },
    sendMessage(conversationId: number, message: string, templateId?: string) {
        dispatch(sendMessage(conversationId, message, templateId));
    },
    updateMessages(messagesFeed: MessagesFeed) {
        dispatch(updateMessages(ownProps.conversationId, {
            before: messagesFeed.messages[0].id,
            after: null
        }));
    },
    updateUnreadCount(conversationId: string, messageId: string) {
        dispatch(updateUnreadCount(conversationId, messageId));
    },
    listenMessages() {
        dispatch(listenMessages(ownProps.conversationId));
    },
    unlistenMessages() {
        dispatch(unlistenMessages(ownProps.conversationId));
    }
});

const LoadingRender = renderComponent(MessagesFeedLoader);
const MessageTemplatesListRender = renderComponent(MessageTemplatesList);

let scrollPosition;

const findScrollContainer = () => {
    return document.getElementById('messagesFeed__scroll');
};

const recordScrollPosition = (scrollContainer: HTMLElement) => {
    if (scrollContainer) { // Record height for updateScrollPosition
        scrollPosition = scrollContainer.scrollHeight - scrollContainer.scrollTop;
    }
};

const updateScrollPosition = (scrollContainer: HTMLElement, messagesFeed: MessagesFeed) => {
    // Initial load, go to bottom
    if (scrollContainer && messagesFeed.messages.length <= 10) {
        scrollToBottom(scrollContainer);
    // Update load, fix in place
    } else if (scrollContainer) { // scrollContainer is undefined if re-showing message templates
        scrollContainer.scrollTop = scrollContainer.scrollHeight - scrollPosition;
    }
};

const scrollToBottom = (scrollContainer: HTMLElement) => {
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
};

let errorListener = null;

const MessagesFeedContainer = compose(
    withRouter,
    withState('twilioError', 'showTwilioError', null),
    withState('haveMessageTemplates', 'setHaveMessageTemplates', undefined),
    connect(mapStateToProps, mapDispatchers),
    getContext({
        session: PropTypes.any.isRequired
    }),
    withHandlers({
        scrollToBottom: props => event => {
            scrollToBottom(findScrollContainer());
        }
    }),
    // helps determine how messages templates should display
    withProps(() => ({
        viewContext: ViewContextTypes.CHAT_STARTERS
    })),
    lifecycle<MessagesContainerProps, {}>({
        async componentWillMount() {
            const hasNextConvoState = (this.props.location.state && this.props.location.state.nextConvo) ? this.props.location.state.nextConvo : null;
            // retrieve errors from past sessions from cache and set the error state if they exist
            const error = await cache.get(this.props.conversationId.toString());
            error && this.props.showTwilioError(error);

            this.props.listenMessages();
            return this.props.loadMessages(hasNextConvoState);
        },
        componentDidMount() {
            errorListener = socket.on(SocketMessages.ERROR, async(error: AppMessengerSocketError) => {
                const { conversationId, session, showTwilioError } = this.props;

                if (conversationId === error.message.conversation_id && session.id === error.message.user_id.toString()) {
                    const errorState = {
                        messageId: error.message.id,
                        conversationId: error.message.conversation_id,
                        templateId: error.message.message_template_id,
                        reason: textToTranslationKey(error.reason)
                    };

                    // FIXME: toString() call due to mismatch between commcontroller socket message and message feed endpoint payload types :(
                    await cache.add(error.message.conversation_id.toString(), errorState, ONE_YEAR);

                    showTwilioError(errorState);
                }
            });
        },
        componentWillUnmount() {
            socket.off(SocketMessages.ERROR, errorListener);
            return this.props.unlistenMessages();
        },
        componentWillReceiveProps(nextProps) {
            if (!this.props.messagesFeed && nextProps.messagesFeed && !this.props.onDesktop) {
                const messages    = nextProps.messagesFeed.messages;
                const lastMessage = messages[messages.length - 1];

                if (lastMessage) {
                    this.props.updateUnreadCount(this.props.conversationId, lastMessage.id);
                }
            }
        },
        componentWillUpdate(nextProps) {
            recordScrollPosition(findScrollContainer());
        },
        componentDidUpdate(prevProps) {
            if (this.props.messagesFeed && this.props.messagesFeed.total_count === 0) {
                return;
            }

            updateScrollPosition(findScrollContainer(), this.props.messagesFeed);

            // Only continue to update unread count when receiving the messagesFeed prop for the first time, when on desktop
            if (!this.props.messagesFeed || !prevProps.messagesFeed || this.props.onDesktop) {
                return;
            }

            const messages = this.props.messagesFeed.messages;
            const newMessage = moment(messages[messages.length - 1].date_added);
            const isNotChatStarter = (prevProps.messagesFeed && prevProps.messagesFeed.messages && prevProps.messagesFeed.messages.length > 1);
            const lastMessage = isNotChatStarter && moment(prevProps.messagesFeed.messages[prevProps.messagesFeed.messages.length - 1].date_added);

            if (isNotChatStarter && newMessage.isAfter(lastMessage)) {
                this.props.updateUnreadCount(this.props.conversationId, messages[messages.length - 1].id);
            }
        }
    }),
    branch(isLoading, LoadingRender),
    branch(showMessageTemplates, MessageTemplatesListRender),
);

export default MessagesFeedContainer(MessagesFeedComponent) as React.ComponentClass<Partial<MessagesContainerProps>>;



// WEBPACK FOOTER //
// ./src/sections/MessagesFeed/index.ts